// Vela steps in automatically when a connection / ad-account problem shows up anywhere in the app:
// the exact error text is sent to her as the owner's message and she replies with the click-by-click fix.
// Once per distinct problem per browser session (so a re-render or a second page visit never re-opens her).
// She remembers what she is fixing (per platform, this browser session) so that after the owner reconnects or
// re-selects an account/Page, `velaConfirmFix` re-runs the real checks and she confirms the result in the SAME conversation.
import api from '@/lib/api';

const KEY = 'vela_guided_issues';
const FIX_KEY = 'vela_active_fixes';
const PENDING_KEY = 'vela_pending_confirm';

const sig = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (Math.imul(31, h) + s.charCodeAt(i)) | 0; return (h >>> 0).toString(36); };
const seen = () => { try { return new Set((sessionStorage.getItem(KEY) || '').split(',').filter(Boolean)); } catch { return new Set(); } };
const remember = (set) => { try { sessionStorage.setItem(KEY, [...set].slice(-40).join(',')); } catch { /* ignore */ } };
const fixes = () => { try { return JSON.parse(sessionStorage.getItem(FIX_KEY) || '{}') || {}; } catch { return {}; } };
const saveFixes = (f) => { try { sessionStorage.setItem(FIX_KEY, JSON.stringify(f)); } catch { /* ignore */ } };

export const PLATFORM_LABEL = { meta: 'Facebook / Instagram ads', meta_ads: 'Facebook / Instagram ads', google: 'Google Ads', google_ads: 'Google Ads', linkedin: 'LinkedIn ads', linkedin_marketing: 'LinkedIn ads', tiktok: 'TikTok ads' };
const PLATFORM_KEY = { meta: 'meta', meta_ads: 'meta', facebook: 'meta', google: 'google', google_ads: 'google', linkedin: 'linkedin', linkedin_marketing: 'linkedin' };
/** meta_ads / google_ads / linkedin_marketing → the readiness engine's platform key (null for anything it doesn't check). */
export const readinessPlatform = (p) => PLATFORM_KEY[p] || null;

export const activeFix = (platform) => { const p = readinessPlatform(platform); return p ? fixes()[p] || null : null; };
const setFix = (p, patch) => { const all = fixes(); all[p] = { ...(all[p] || {}), ...patch, at: Date.now() }; saveFixes(all); return all[p]; };
export const clearFix = (platform) => {
  const p = readinessPlatform(platform);
  if (!p) return;
  const all = fixes();
  const s = seen();
  (all[p]?.sigs || []).forEach((id) => s.delete(id)); // the same problem may legitimately come back later → Vela guides again
  remember(s);
  delete all[p];
  saveFixes(all);
};
/** VelaChat calls this once the guide conversation has a session, so the verdict lands in that same thread. */
export const rememberFixSession = (platform, sessionId) => { const p = readinessPlatform(platform); if (p && sessionId && fixes()[p]) setFix(p, { sessionId }); };

/**
 * @param {object} o
 * @param {string} o.source   where the owner saw it ("the Campaigns page", "Settings → Integrations", "the launch of “Spring promo”")
 * @param {string[]} o.problems  the exact error / blocker lines shown to the owner
 * @param {string} [o.platform]  meta | google | linkedin | …
 * @param {string} [o.key]   extra de-dupe key (e.g. campaign id)
 */
export function velaGuide({ source, problems, platform, key = '' }) {
  const list = (problems || []).map((p) => String(p || '').trim()).filter(Boolean);
  if (!list.length) return false;
  const id = sig(`${platform || ''}|${key}|${list.join('|')}`);
  const p = readinessPlatform(platform);
  let thread = null;
  if (p) { const cur = fixes()[p] || {}; thread = cur.sessionId || null; setFix(p, { problems: list, source, sigs: [...new Set([...(cur.sigs || []), id])].slice(-10) }); }
  const s = seen();
  if (s.has(id)) return false;
  s.add(id); remember(s);
  const where = PLATFORM_LABEL[platform] ? `${PLATFORM_LABEL[platform]} on ${source}` : source;
  const say = `Vela, something's wrong with ${where} and I need you to walk me through fixing it step by step — where exactly do I click, in what order, and how do I know it worked? Here's what Veluro shows me: ${list.map((p, i) => `(${i + 1}) ${p}`).join(' ')}`;
  // A new problem on a platform Vela is already fixing continues in that same thread (she knows what was done so far).
  window.dispatchEvent(new CustomEvent('vela:open', { detail: { say, sessionId: thread || undefined, guide: { source, platform: platform || null } } }));
  return true;
}

/**
 * After the owner acted (reconnected, picked another account/Page, pressed Re-check): re-run the real readiness checks for
 * that platform and let Vela confirm — "✅ all clear" or exactly what is still open — in the conversation she was fixing it in.
 * The backend stays quiet (`skipped`) when nothing had been flagged for that platform (routine first connection).
 * trigger: reconnected | selection | recheck | manual
 */
export async function velaConfirmFix({ platform, trigger = 'reconnected' }) {
  const p = readinessPlatform(platform);
  if (!p) return null;
  const fix = fixes()[p] || null;
  let res;
  try {
    res = (await api.post('/vela/fix-recheck', { platform: p, trigger, session_id: fix?.sessionId || '', problems: fix?.problems || [], flagged: !!fix })).data;
  } catch { return null; }
  if (!res || res.skipped) return res || null;
  window.dispatchEvent(new CustomEvent('vela:readiness', { detail: { platform: p, result: res } }));
  if (res.ready) clearFix(p); else setFix(p, { problems: res.still_failing || [], sessionId: res.session_id, source: fix?.source || 'Settings → Integrations' });
  window.dispatchEvent(new CustomEvent('vela:open', { detail: { sessionId: res.session_id, guide: { source: 'recheck', platform: p, verdict: res.ready ? 'ready' : 'open', trigger } } }));
  return res;
}

/** The page is about to reload (OAuth popup success) — confirm right after it comes back. */
export const queueConfirmFix = (platform, trigger = 'reconnected') => { const p = readinessPlatform(platform); if (p) { try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ platform: p, trigger })); } catch { /* ignore */ } } };
export const runPendingConfirmFix = () => {
  let pending = null;
  try { pending = JSON.parse(sessionStorage.getItem(PENDING_KEY) || 'null'); sessionStorage.removeItem(PENDING_KEY); } catch { pending = null; }
  if (pending?.platform) velaConfirmFix(pending);
};

/** Deep link from emails: /campaigns?vela_fix=linkedin opens Vela on the page's current blockers (handled by the readiness banner). */
export const velaFixParam = () => { try { return new URLSearchParams(window.location.search).get('vela_fix'); } catch { return null; } };
